import { useEffect, useRef } from 'react';
import { useActiveTheme } from '@/hooks/usePageBuilder';
import { getThemeStyle, getThemeFontsUrl } from '@/config/themeStyles';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { data: activeTheme } = useActiveTheme();
  const fontLinkRef = useRef<HTMLLinkElement | null>(null);

  useEffect(() => {
    if (!activeTheme?.store_themes) return;

    const theme = activeTheme.store_themes;
    const themeSlug = theme.slug;
    const themeStyle = getThemeStyle(themeSlug);
    const colors = theme.colors as Record<string, string> | null;
    const customColors = activeTheme.custom_colors as Record<string, string> | null;

    // Merge theme colors with custom overrides
    const finalColors = { ...colors, ...customColors };

    const root = document.documentElement;

    // ===== 1. LOAD THEME FONTS =====
    const fontUrl = getThemeFontsUrl(themeSlug);
    if (fontUrl) {
      if (fontLinkRef.current) {
        fontLinkRef.current.href = fontUrl;
      } else {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = fontUrl;
        document.head.appendChild(link);
        fontLinkRef.current = link;
      }
    }

    // ===== 2. SET THEME CLASS ON BODY =====
    // Remove all theme classes
    document.body.className = document.body.className
      .split(' ')
      .filter(c => !c.startsWith('theme-'))
      .join(' ');
    // Add current theme class
    document.body.classList.add(`theme-${themeSlug}`);

    // ===== 3. APPLY COLORS =====
    if (finalColors && Object.keys(finalColors).length > 0) {
      const colorMappings: Record<string, string[]> = {
        'primary': ['--primary', '--ring', '--sidebar-primary'],
        'accent': ['--accent', '--warning'],
        'background': ['--background'],
        'foreground': ['--foreground', '--card-foreground', '--popover-foreground'],
      };

      Object.entries(finalColors).forEach(([key, value]) => {
        if (!value) return;

        const cssVars = colorMappings[key];
        if (cssVars) {
          cssVars.forEach(cssVar => {
            root.style.setProperty(cssVar, value);
          });
        }

        // Also set primary-glow based on primary
        if (key === 'primary') {
          const parts = value.split(' ');
          if (parts.length >= 3) {
            const hue = parts[0];
            const sat = parts[1];
            root.style.setProperty('--primary-glow', `${hue} ${sat} 50%`);
          }
        }
      });

      // Update gradients based on primary color
      if (finalColors.primary) {
        const parts = finalColors.primary.split(' ');
        if (parts.length >= 3) {
          const hue = parts[0];
          const sat = parts[1].replace('%', '');
          const light = parseInt(parts[2].replace('%', ''));
          
          root.style.setProperty('--gradient-primary', 
            `linear-gradient(135deg, hsl(${hue} ${sat}% ${light}%) 0%, hsl(${hue} ${sat}% ${light - 8}%) 100%)`
          );
          root.style.setProperty('--gradient-hero', 
            `linear-gradient(135deg, hsl(${hue} ${sat}% ${light - 6}%) 0%, hsl(${parseInt(hue) + 15} ${parseInt(sat) - 10}% ${light - 10}%) 50%, hsl(${parseInt(hue) + 30} ${parseInt(sat) - 20}% ${light - 15}%) 100%)`
          );
        }
      }

      if (finalColors.accent) {
        const parts = finalColors.accent.split(' ');
        if (parts.length >= 3) {
          const hue = parts[0];
          const sat = parts[1].replace('%', '');
          const light = parseInt(parts[2].replace('%', ''));
          
          root.style.setProperty('--gradient-accent', 
            `linear-gradient(135deg, hsl(${hue} ${sat}% ${light}%) 0%, hsl(${parseInt(hue) - 10} ${sat}% ${light - 5}%) 100%)`
          );
        }
      }
    }

    // ===== 4. APPLY TYPOGRAPHY =====
    root.style.setProperty('--font-heading', themeStyle.fonts.heading);
    root.style.setProperty('--font-body', themeStyle.fonts.body);
    root.style.setProperty('--font-heading-weight', themeStyle.fonts.headingWeight.toString());

    // ===== 5. APPLY BORDER RADIUS =====
    root.style.setProperty('--theme-radius-card', themeStyle.radius.card);
    root.style.setProperty('--theme-radius-button', themeStyle.radius.button);
    root.style.setProperty('--theme-radius-badge', themeStyle.radius.badge);
    root.style.setProperty('--theme-radius-input', themeStyle.radius.input);

    // ===== 6. APPLY SHADOWS =====
    root.style.setProperty('--theme-shadow-card', themeStyle.shadows.card);
    root.style.setProperty('--theme-shadow-button', themeStyle.shadows.button);
    root.style.setProperty('--theme-shadow-hover', themeStyle.shadows.hover);

    // ===== 7. APPLY ANIMATIONS =====
    root.style.setProperty('--theme-hover-transform', themeStyle.animations.cardHover);
    root.style.setProperty('--theme-button-hover', themeStyle.animations.buttonHover);

    // ===== 8. APPLY CUSTOM CSS VARS =====
    Object.entries(themeStyle.cssVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // ===== 9. APPLY SPECIAL EFFECTS =====
    if (themeStyle.effects.neonGlow) {
      root.classList.add('theme-effect-neon');
    } else {
      root.classList.remove('theme-effect-neon');
    }

    if (themeStyle.effects.glassmorphism) {
      root.classList.add('theme-effect-glass');
    } else {
      root.classList.remove('theme-effect-glass');
    }

    if (themeStyle.effects.gradientText) {
      root.classList.add('theme-effect-gradient-text');
    } else {
      root.classList.remove('theme-effect-gradient-text');
    }

    // Cleanup function
    return () => {
      // Optional: reset to defaults
    };
  }, [activeTheme]);

  return <>{children}</>;
}
